import { CommonModule } from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  forwardRef,
  inject,
  Input,
  OnChanges,
  OnInit,
  Provider,
  SimpleChanges,
  ViewChild
} from '@angular/core';
import { AbstractControl, FormArray, FormControl, NG_VALUE_ACCESSOR, UntypedFormGroup, ValidationErrors } from '@angular/forms';
import { NgbDateAdapter, NgbDateNativeAdapter, NgbDateParserFormatter, NgbTimeAdapter } from '@ng-bootstrap/ng-bootstrap';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { I18NextPipe } from 'angular-i18next';
import { isNil } from 'lodash';
import {
  AccordionComponent,
  AccordionPanelComponent,
  AlertComponent,
  CheckboxComponent,
  CheckboxDescription,
  DatepickerComponent,
  EnumHelper,
  FormGroupComponent,
  FormsUtil,
  InputLabelComponent,
  MbsValidators,
  ModalService,
  ModalSettings,
  NumberComponent,
  RadioComponent,
  RadioDescription,
  SelectComponent,
  ShortDateParserFormatter,
  SwitcherComponent,
  TimepickerComponent
} from 'mbs-ui-kit';
import { NgbTimeStringAdapterFormat12 } from 'mbs-ui-kit/form/timepicker/ngb-time-adapters/ngb-time-string-adapter-format-12';
import { ElementsSelectors } from 'mbs-ui-kit/models/ElementSelectors';
import { AdvancedRecurTypeText, RecurType } from 'mbs-ui-shared@models/RecurType.enum';
import { DailyFrequency, MonthlyFrequency, RecurringSchedule, WeeklyFrequency } from 'mbs-ui-shared@models/ScheduleModel';
import moment from 'moment';
import { noop } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { StepsHelpers } from 'wizards/helpers/steps-helpers';
import { DayOfWeek } from 'wizards/models/DayOfWeek.enum';
import {
  HoursOrMinutesItems,
  OccursEveryPeriodType,
  ScheduleAdvancedStepValue,
  ScheduleFormSettings
} from 'wizards/models/schedule-advanced-models';
import { WeekNumber } from 'wizards/models/WeekNumber.enum';
import { RemoteManagementWizardsService } from 'wizards/services/remote-management-wizards.service';
import { FormPipeOperators, StepBase } from 'wizards/steps/StepBase.class';

import { ReactiveFormsModule } from '@angular/forms';

const ScheduleAdvancedStepValueAccessor: Provider = {
  provide: NG_VALUE_ACCESSOR,
  // eslint-disable-next-line @typescript-eslint/no-use-before-define
  useExisting: forwardRef(() => ScheduleAdvancedStepComponent),
  multi: true
};

export const getScheduleFormControls = () => {
  return {
    RecurringPeriod: new FormControl('Daily'),
    Occurrence: new FormControl(1),
    DayOfWeek: new FormControl(3),
    DayOfMonthCount: new FormControl(1),
    WeeklyDayOfWeek: new FormArray([]),
    RepeatEveryCount: new FormControl(1),
    StartFromDate: new FormControl(moment().format('YYYY-MM-DDT00:00:00')),
    DailyFreqPeriodOption: new FormControl('OccursAt'),
    OccursAtTime: new FormControl('00:00', MbsValidators.timeValidatorWithoutText),
    OccursEveryCount: new FormControl({ value: 0, disabled: true }),
    OccursEveryPeriod: new FormControl({ value: 'hours', disabled: true }),
    OccursEveryFromTime: new FormControl({ value: '00:00', disabled: true }, MbsValidators.timeValidatorWithoutText),
    OccursEveryToTime: new FormControl({ value: '23:59', disabled: true }, MbsValidators.timeValidatorWithoutText)
  };
};

export const dayOfMonth = 'Day Of Month';

@UntilDestroy()
@Component({
  selector: 'mbs-schedule-advanced-step',
  templateUrl: './schedule-advanced-step.component.html',
  providers: [
    ScheduleAdvancedStepValueAccessor,
    { provide: NgbDateParserFormatter, useClass: ShortDateParserFormatter },
    { provide: NgbTimeAdapter, useClass: NgbTimeStringAdapterFormat12 },
    { provide: NgbDateAdapter, useClass: NgbDateNativeAdapter }
  ],
  imports: [
    AccordionPanelComponent,
    AlertComponent,
    CheckboxComponent,
    CheckboxDescription,
    CommonModule,
    DatepickerComponent,
    FormGroupComponent,
    InputLabelComponent,
    NumberComponent,
    RadioComponent,
    RadioDescription,
    ReactiveFormsModule,
    SelectComponent,
    SwitcherComponent,
    TimepickerComponent,
    AccordionComponent,
    I18NextPipe
  ]
})
export class ScheduleAdvancedStepComponent extends StepBase<ScheduleAdvancedStepValue> implements OnInit, OnChanges {
  i18nPipe = inject(I18NextPipe);
  mainService: RemoteManagementWizardsService;
  modalService = inject(ModalService);
  protected cd = inject(ChangeDetectorRef);

  @Input() copyOnly: boolean;

  @ViewChild(AccordionComponent, { static: true }) accordion: AccordionComponent;

  public readonly maxHours = 23;
  public readonly maxMinutes = 1439;
  public readonly elementsSelector: ElementsSelectors = {
    forceFull: {
      infoString: 'ff_infoString',
      monthly: 'ff_monthly',
      dayOfMonthCount: 'ff_dayOfMonthCount',
      dayOfWeek: 'ff_dayOfWeek',
      notUnixOrMonthly: 'ff_notUnixOrMonthly',
      weekly: 'ff_weekly'
    },
    blockLevel: {
      infoString: 'bl_infoString',
      monthly: 'bl_monthly',
      dayOfMonthCount: 'bl_dayOfMonthCount',
      dayOfWeek: 'bl_dayOfWeek',
      notUnixOrMonthly: 'bl_notUnixOrMonthly',
      weekly: 'bl_weekly'
    },
    scheduleDiff: {
      infoString: 'sd_infoString',
      monthly: 'sd_monthly',
      dayOfMonthCount: 'sd_dayOfMonthCount',
      dayOfWeek: 'sd_dayOfWeek',
      notUnixOrMonthly: 'sd_notUnixOrMonthly',
      weekly: 'sd_weekly'
    },
    scheduleTLog: {
      infoString: 'st_infoString',
      monthly: 'st_monthly',
      dayOfMonthCount: 'st_dayOfMonthCount',
      dayOfWeek: 'st_dayOfWeek',
      notUnixOrMonthly: 'st_notUnixOrMonthly',
      weekly: 'st_weekly'
    },
    other: {
      backupNotUnix: 'backupNotUnix'
    }
  };

  public scheduleSettingsForm = new UntypedFormGroup({
    forceFull: new UntypedFormGroup(getScheduleFormControls()),
    blockLevel: new UntypedFormGroup(getScheduleFormControls()),
    scheduleDiff: new UntypedFormGroup(getScheduleFormControls()),
    scheduleTLog: new UntypedFormGroup(getScheduleFormControls())
  });

  public subtitleByType = this.i18nPipe.transform('wizards:backup');

  public hoursOrMinutesItems = HoursOrMinutesItems;
  public RecurringPeriodItems = ['Daily', 'Weekly', 'Monthly'];
  public DaysOfWeekEnumArray = EnumHelper.EnumToSelectIndexesArray(DayOfWeek);
  public WeekNumberEnumArray = EnumHelper.EnumToSelectIndexesArray(WeekNumber).sort((a, b) =>
    a.label === WeekNumber[-1] || b.label === 'Penultimate' ? -1 : 0
  );

  public panelNames = [];

  public recurringScheduleError = '';

  private needSelectDayModalSettings: ModalSettings = {
    header: { title: this.i18nPipe.transform('wizards:scheduleRecurringOptions') },
    footer: { okButton: { text: 'Ok' }, cancelButton: { show: false } }
  };

  private myPanelsInfo = {
    forceFull: { tittle: this.i18nPipe.transform('wizards:incrementalBase'), infoString: '', isOpen: false },
    blockLevel: { tittle: this.i18nPipe.transform('wizards:blockLevelBase'), infoString: '', isOpen: false },
    scheduleDiff: { tittle: this.i18nPipe.transform('wizards:diffLevelBase'), infoString: '', isOpen: false },
    scheduleTLog: { tittle: this.i18nPipe.transform('wizards:tLogBase'), infoString: '', isOpen: false }
  };

  public get panelsInfo() {
    return this.myPanelsInfo;
  }

  constructor() {
    const mainService = inject(RemoteManagementWizardsService);

    super();
    this.mainService = mainService;

    this.setPanelsInfo();
    this.setSubtitleType();
  }

  setPanelsInfo(): void {
    if (this.isBackupPlan) return;

    if (this.isRestore) {
      this.panelsInfo.forceFull.tittle = this.i18nPipe.transform('wizards:scheduleRecurringOptions');
      return void (this.panelsInfo.blockLevel.tittle = this.i18nPipe.transform('wizards:blockLevelRestoreIncremental'));
    }

    if (this.isConsistencyCheck) {
      return void (this.panelsInfo.forceFull.tittle = this.i18nPipe.transform('wizards:recurringSchedule'));
    }

    this.panelsInfo.forceFull.tittle = this.i18nPipe.transform('wizards:fullSchedule');
    this.panelsInfo.blockLevel.tittle = this.i18nPipe.transform('wizards:blockLevelIncremental');
  }

  consistencyCheckValidator(control: AbstractControl): ValidationErrors | null {
    return this.isConsistencyCheck && !control?.value ? { shouldBeEnabled: true } : null;
  }

  stepFormValidator(formGroup: UntypedFormGroup): ValidationErrors | null {
    const value = formGroup.value;
    for (const key in value) {
      if (this.isScheduleSettingsKey(key) && !isNil(value[key].Enabled)) {
        const schedule = value[key].FormSchedule;
        if (
          value[key].Enabled &&
          schedule &&
          schedule.RecurringPeriod === RecurType.Weekly &&
          !schedule.WeeklyFrequency.DaysOfWeek.length
        ) {
          return { needSelectDay: { message: this.myPanelsInfo[key].tittle } };
        }
      }
    }
    return null;
  }

  ngOnInit(): void {
    if (!this.isUnix || this.isNetBuild) this.RecurringPeriodItems.push('Yearly');

    this.initForm();
  }

  protected getPipeOperators(): FormPipeOperators {
    return [untilDestroyed(this), debounceTime(200)];
  }

  initForm(): void {
    this.stepForm = new UntypedFormGroup(
      {
        forceFull: new UntypedFormGroup({
          Enabled: new FormControl(false, this.consistencyCheckValidator.bind(this)),
          FormSchedule: new FormControl(null)
        }),
        blockLevel: new UntypedFormGroup({
          Enabled: new FormControl(false),
          FormSchedule: new FormControl(null)
        }),
        scheduleDiff: new UntypedFormGroup({
          Enabled: new FormControl(false),
          FormSchedule: new FormControl(null)
        }),
        scheduleTLog: new UntypedFormGroup({
          Enabled: new FormControl(false),
          FormSchedule: new FormControl(null)
        }),
        ForceFullApplyDiffSizeCondition: new FormControl(false),
        ForceFullDiffSizeCondition: new FormControl(50)
      },
      this.stepFormValidator.bind(this)
    );

    this.initFormEvents();
  }

  protected initFormEvents(): void {
    super.initFormEvents();

    this.stepForm.controls['ForceFullApplyDiffSizeCondition'].valueChanges.pipe(untilDestroyed(this)).subscribe({
      next: (value) => {
        const action = value ? 'enable' : 'disable';
        this.stepForm.get('ForceFullDiffSizeCondition')[action]();
      }
    });

    (this.stepForm.controls['forceFull'] as UntypedFormGroup).controls['Enabled'].valueChanges.pipe(untilDestroyed(this)).subscribe({
      next: (enabled: boolean) => {
        this.toggleDisabledPanelStatesForNonForceFullPanels(enabled);
      }
    });
    (this.stepForm.controls['blockLevel'] as UntypedFormGroup).controls['Enabled'].valueChanges.pipe(untilDestroyed(this)).subscribe({
      next: (enabled) => {
        if ((!this.isUnix || this.isNetBuild) && this.isBackupPlan) {
          this.toggleDisabledStateForForceFullDiffSizeCondition(enabled);
        }
      }
    });
  }

  onStepFormChange(value: ScheduleAdvancedStepValue): void {
    this.value = { ...value, valid: this.stepForm.valid };

    if (!this.panelNames.length) {
      for (const key in value) {
        if (this.isScheduleSettingsKey(key) && !isNil(value[key].Enabled) && (!this.isRestore || key === 'forceFull'))
          this.initScheduleFormAndPanelNames(value[key], key);
      }

      this.toggleDisabledPanelStatesForNonForceFullPanels(value.forceFull.Enabled);

      if (this.copyOnly && this.isSQLPlan && this.stepForm) {
        this.toggleFormControls(['scheduleDiff'], false);
        this.switcherChangeHandler(false, 'scheduleDiff');
      }
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (this.stepForm && this.isConsistencyCheck && changes?.stepFocused) {
      this.switcherChangeHandler(this.stepForm.get('forceFull').value.Enabled, 'forceFull', true);
    }

    if (changes.copyOnly && this.isSQLPlan && this.stepForm) {
      if (changes.copyOnly.currentValue && this.myPanelsInfo.scheduleDiff.isOpen) {
        this.togglePanel(false, 'scheduleDiff');
        this.accordion.collapse('scheduleDiff');
      }

      this.toggleFormControls(['scheduleDiff'], !changes.copyOnly.currentValue);
      this.switcherChangeHandler(!changes.copyOnly.currentValue && this.stepForm.get('scheduleDiff').value.Enabled, 'scheduleDiff', false);
    }
  }

  updateForm(value: ScheduleAdvancedStepValue): void {
    if (this.isConsistencyCheck && value.forceFull) value.forceFull.Enabled = true;

    for (const key in value) {
      if (value[key] && value[key].FormSchedule)
        value[key].FormSchedule.RecurringPeriod = this.getRecurringPeriod(value[key].FormSchedule.RecurringPeriod);
    }

    this.stepForm.reset(value);

    if (this.isBackupPlan && !isNil(value.blockLevel?.Enabled)) this.switcherChangeHandler(value.blockLevel.Enabled, 'blockLevel');
    if (this.isConsistencyCheck && value.forceFull) this.switcherChangeHandler(value.forceFull.Enabled, 'forceFull', true);
  }

  getRecurringPeriod(period: RecurType): RecurType {
    return !period ||
      period === RecurType.Periodically ||
      period === RecurType.Instantly ||
      (this.isUnix && !this.isNetBuild && period === RecurType.Yearly)
      ? RecurType.Daily
      : period === RecurType.DayOfMonth
        ? RecurType.Monthly
        : period;
  }

  forceValid(): void {
    if (!this.stepForm.valid && this.stepForm?.errors?.needSelectDay) {
      this.modalService
        .confirm(
          this.needSelectDayModalSettings,
          this.isRestore
            ? this.i18nPipe.transform('wizards:shouldBeSelectedRestore')
            : this.i18nPipe.transform('wizards:shouldBeSelectedBackup', { name: this.stepForm.errors.needSelectDay.message })
        )
        .then()
        .catch(noop);
    }
    this.consistencyCheckShowError();
    FormsUtil.triggerValidation(this.stepForm);
    this.resetFormsValidState();
  }

  resetFormsValidState(): void {
    if (!this.value || !this.value.valid) return;

    const formValue = this.scheduleSettingsForm.getRawValue();
    for (const key in formValue) {
      if (key !== 'valid') this.scheduleSettingsForm.get(key).reset(formValue[key]);
    }

    const applyDiffSizeControl = this.stepForm.get('ForceFullApplyDiffSizeCondition') as FormControl;
    if (applyDiffSizeControl && applyDiffSizeControl.enabled && applyDiffSizeControl.value) {
      this.stepForm.get('ForceFullDiffSizeCondition').reset(this.stepForm.get('ForceFullDiffSizeCondition').value);
    }
  }

  initScheduleFormAndPanelNames(formData: ScheduleFormSettings, key: string): void {
    this.updateScheduleFormData(key, formData.FormSchedule);
    this.panelNames.push(key);
    this.scheduleSettingsForm
      .get(key)
      .valueChanges.pipe(untilDestroyed(this), debounceTime(200))
      .subscribe((value) => {
        const formGroup = this.stepForm.get(key) as UntypedFormGroup;
        const newFormSchedule = this.getUpdatedModel(formData.FormSchedule, value);
        formGroup.get('FormSchedule').setValue(newFormSchedule);
        this.setBackupScheduleString(key);
      });
    this.scheduleSettingsForm.get(key).updateValueAndValidity();
  }

  updateScheduleFormData(key: string, newFormData: RecurringSchedule): void {
    const period = RecurType[newFormData.RecurringPeriod || 1];
    const frequency = `${period}Frequency`;
    const form = this.scheduleSettingsForm.get(key) as UntypedFormGroup;
    const isMonthly = period === 'Monthly';
    form.get('RecurringPeriod').setValue(period, { emitEvent: false });

    if (!this.isUnix || this.isNetBuild || isMonthly) {
      form.get('RepeatEveryCount').setValue(newFormData[frequency].RepeatEveryCount || 1, { emitEvent: false });
      form.get('StartFromDate').setValue(newFormData[frequency].StartFromDate, { emitEvent: false });
    }

    if (isMonthly) {
      form.get('Occurrence').setValue(newFormData[frequency].OccurrencePeriod || 0, { emitEvent: false });
      form.get('DayOfMonthCount').setValue(newFormData[frequency].DayOfMonthCount || 1, { emitEvent: false });
      form
        .get('DayOfWeek')
        .setValue(
          (typeof newFormData[frequency].DayOfWeek === 'string'
            ? DayOfWeek[newFormData[frequency].DayOfWeek]
            : newFormData[frequency].DayOfWeek) || 0,
          { emitEvent: false }
        );
    }

    if (period === 'Weekly') {
      const WeeklyDayOfWeek = form.get('WeeklyDayOfWeek') as FormArray;
      WeeklyDayOfWeek.clear();
      this.DaysOfWeekEnumArray.forEach((elem, idx) => {
        WeeklyDayOfWeek.push(new FormControl(newFormData[frequency].DaysOfWeek.some((day) => day === idx)));
      });
    }

    this.updateDailyFrequencyFormSide(newFormData.DailyFrequency, form);
    this.dailyFreqPeriodChangeHandler(newFormData.DailyFrequency.PeriodOption || 'OccursAt', key);
    this.updateWeeklyGroup(form);
  }

  updateDailyFrequencyFormSide(dailyFrequency: DailyFrequency, form: UntypedFormGroup): void {
    form.get('DailyFreqPeriodOption').setValue(dailyFrequency.PeriodOption || 'OccursAt', { emitEvent: false });
    form.get('OccursAtTime').setValue(dailyFrequency.OccursAtTime || '12:00 AM', { emitEvent: false });
    form.get('OccursEveryCount').setValue(dailyFrequency.OccursEveryCount || 0, { emitEvent: false });
    form.get('OccursEveryPeriod').setValue(dailyFrequency.OccursEveryPeriod || 'hours', { emitEvent: false });
    form.get('OccursEveryFromTime').setValue(dailyFrequency.OccursEveryFromTime || '00:00', { emitEvent: false });
    form.get('OccursEveryToTime').setValue(dailyFrequency.OccursEveryToTime || '23:59', { emitEvent: false });
  }

  dailyFreqPeriodChangeHandler(event: string, key: string): void {
    const form = this.scheduleSettingsForm.get(key) as UntypedFormGroup;
    if (event === 'OccursAt') {
      form.get('OccursAtTime').enable();
      form.get('OccursEveryCount').disable();
      form.get('OccursEveryPeriod').disable();
      form.get('OccursEveryFromTime').disable();
      form.get('OccursEveryToTime').disable();
    } else if (event === 'OccursEvery') {
      form.get('OccursAtTime').disable();
      form.get('OccursEveryCount').enable();
      form.get('OccursEveryPeriod').enable();
      form.get('OccursEveryFromTime').enable();
      form.get('OccursEveryToTime').enable();
    }
  }

  switcherChangeHandler(open: boolean, panelId: string, needExpand = true): void {
    this.updateConsistencyErrors();
    this.togglePanel(open, panelId);

    if (!this.myPanelsInfo[panelId].infoString || !open) {
      this.scheduleSettingsForm.get(panelId).updateValueAndValidity();
    }

    if (needExpand) {
      // Can use without setTimeout only if remove [disabled]="panel.disabled" from ngb-panel into mbs-accordion
      if (open) {
        return void setTimeout(() => this.accordion.expand(panelId));
      }

      this.accordion.collapse(panelId);
    }
  }

  updateWeeklyGroup(form: UntypedFormGroup): void {
    const WeeklyDayOfWeek = form.get('WeeklyDayOfWeek') as FormArray;
    this.DaysOfWeekEnumArray.forEach(() => WeeklyDayOfWeek.push(new FormControl(false)));
  }

  toggleDisabledPanelStatesForNonForceFullPanels(enabled: boolean): void {
    this.panelNames.forEach((name) => {
      if (name !== 'forceFull' && (!this.copyOnly || name !== 'scheduleDiff' || !this.isSQLPlan)) {
        const panel = this.stepForm.get(name) as UntypedFormGroup;
        if (!enabled) {
          panel.get('Enabled').setValue(false);
          panel.disable();
        } else panel.enable();
      }
    });
  }

  toggleDisabledStateForForceFullDiffSizeCondition(enable: boolean): void {
    const action = enable ? 'enable' : 'disable';
    this.stepForm.get('ForceFullApplyDiffSizeCondition')[action]();
    if (!enable) {
      this.stepForm.get('ForceFullApplyDiffSizeCondition').setValue(enable);
    }
    if (this.stepForm.get('ForceFullApplyDiffSizeCondition').value) this.stepForm.get('ForceFullDiffSizeCondition')[action]();
  }

  toggleChangePanel(event): void {
    this.togglePanel(event.nextState, event.panelId);
  }

  togglePanel(open = true, panelId: string): void {
    this.myPanelsInfo[panelId].isOpen = open;
  }

  getPanelTitle(panelId: string): string {
    return this.myPanelsInfo[panelId].tittle;
  }

  setBackupScheduleString(key: string): void {
    const form = this.scheduleSettingsForm.get(key) as UntypedFormGroup;
    this.myPanelsInfo[key].infoString = StepsHelpers.getStringByScheduleData(form.value);
  }

  getCounterText(name: string): string {
    const formValue = this.stepForm.get(name).value;
    return formValue.FormSchedule ? AdvancedRecurTypeText[RecurType[formValue.FormSchedule.RecurringPeriod]] : '';
  }

  getUpdatedModel(schedule: RecurringSchedule, formValue): RecurringSchedule {
    const isDaily = formValue.RecurringPeriod === 'Daily';
    const isWeekly = formValue.RecurringPeriod === 'Weekly';
    const isMonthly = formValue.RecurringPeriod === 'Monthly';

    const model: RecurringSchedule = {
      RecurringPeriod: +RecurType[formValue.RecurringPeriod],
      DailyFrequency: this.getDailyFrequency(isDaily, formValue, schedule.DailyFrequency) as DailyFrequency,
      WeeklyFrequency: this.getWeeklyFrequency(isWeekly, formValue, schedule.WeeklyFrequency),
      MonthlyFrequency: this.getMonthlyFrequency(isMonthly, formValue, schedule.MonthlyFrequency)
    };

    if (!this.isUnix || this.isNetBuild || (isMonthly && !this.isRestore)) {
      model.YearlyFrequency = { RepeatEveryCount: formValue.RepeatEveryCount, StartFromDate: formValue.StartFromDate };
    }

    if (model.RecurringPeriod === RecurType.Monthly && model.MonthlyFrequency.OccurrencePeriod === WeekNumber[dayOfMonth]) {
      model.RecurringPeriod = RecurType.DayOfMonth;
    }

    return model;
  }

  getDailyFrequency(isDaily: boolean, formValue, dailyFrequency): Partial<DailyFrequency> {
    const data: Partial<DailyFrequency> = {
      PeriodOption: formValue.DailyFreqPeriodOption || dailyFrequency.PeriodOption,
      OccursAtTime: formValue.OccursAtTime || dailyFrequency.OccursAtTime,
      OccursEveryCount: formValue.OccursEveryCount || dailyFrequency.OccursEveryCount,
      OccursEveryPeriod: formValue.OccursEveryPeriod || dailyFrequency.OccursEveryPeriod,
      OccursEveryFromTime: formValue.OccursEveryFromTime || dailyFrequency.OccursEveryFromTime,
      OccursEveryToTime: formValue.OccursEveryToTime || dailyFrequency.OccursEveryToTime
    };

    if (!this.isUnix || this.isNetBuild) {
      data.StartFromDate = isDaily ? formValue.StartFromDate : dailyFrequency.StartFromDate;
      data.RepeatEveryCount = isDaily ? formValue.RepeatEveryCount : dailyFrequency.RepeatEveryCount;
    }

    return data;
  }

  getWeeklyFrequency(isWeekly: boolean, formValue, weeklyFrequency): WeeklyFrequency {
    const daysOfWeek: DayOfWeek[] = [];

    if (isWeekly) {
      formValue.WeeklyDayOfWeek?.forEach(
        (dayCheck, idx) => dayCheck && daysOfWeek.push(DayOfWeek[this.DaysOfWeekEnumArray[idx].label as string])
      );
    }

    const data: WeeklyFrequency = { DaysOfWeek: isWeekly ? daysOfWeek : weeklyFrequency.DaysOfWeek };

    if (!this.isUnix || this.isNetBuild) {
      data.StartFromDate = isWeekly ? formValue.StartFromDate : weeklyFrequency.StartFromDate;
      data.RepeatEveryCount = isWeekly ? formValue.RepeatEveryCount : weeklyFrequency.RepeatEveryCount;
    }

    return data;
  }

  getMonthlyFrequency(isMonthly: boolean, formValue, monthlyFrequency): MonthlyFrequency {
    const data: MonthlyFrequency = {
      OccurrencePeriod: isMonthly ? formValue.Occurrence : monthlyFrequency.OccurrencePeriod,
      DayOfWeek: isMonthly ? DayOfWeek[formValue.DayOfWeek] : monthlyFrequency.DayOfWeek,
      DayOfMonthCount: isMonthly ? formValue.DayOfMonthCount || monthlyFrequency.DayOfMonthCount : monthlyFrequency.DayOfMonthCount
    };
    data.StartFromDate = isMonthly ? formValue.StartFromDate : monthlyFrequency.StartFromDate;
    data.RepeatEveryCount = isMonthly ? formValue.RepeatEveryCount : monthlyFrequency.RepeatEveryCount;
    return data;
  }

  recurringPeriodChangeHandler(event, name): void {
    if (!this.scheduleSettingsForm.get(name) || (this.isUnix && !this.isNetBuild)) return;

    const control = this.scheduleSettingsForm.get(name).get('RepeatEveryCount');
    if (!control.value) return;

    if (event === 'Daily' && control.value > 31) return control.setValue(31);
    if (event === 'Weekly' && control.value > 52) return control.setValue(52);
    if (event === 'Monthly' && control.value > 12) return control.setValue(12);
    if (event === 'Yearly' && control.value > 99) return control.setValue(99);
  }

  occursEveryPeriodChangeHandler(period: OccursEveryPeriodType, name: string): void {
    if (period !== 'hours') return;

    const countControl: FormControl<number> = this.scheduleSettingsForm.get(name)['controls'].OccursEveryCount;

    if (countControl.value > this.maxHours) countControl.setValue(this.maxHours);
  }

  private isScheduleSettingsKey(key: string): boolean {
    return key !== 'ForceFullApplyDiffSizeCondition' && key !== 'ForceFullDiffSizeCondition';
  }

  private setSubtitleType(): void {
    if (this.isConsistencyCheck) {
      return void (this.subtitleByType = this.i18nPipe.transform('wizards:consistencyCheck.consistencyLabel').toLowerCase());
    }

    if (this.isRestore) this.subtitleByType = this.i18nPipe.transform('wizards:restore');
  }

  private updateConsistencyErrors(): void {
    if (!this.isConsistencyCheck || !this.recurringScheduleError) return;

    this.recurringScheduleError = '';
  }

  private consistencyCheckShowError(): void {
    if (this.stepForm.valid) return;

    const control = this.stepForm?.get('forceFull')?.get('Enabled');
    if (!control?.errors?.shouldBeEnabled) return;

    this.recurringScheduleError = this.i18nPipe.transform('wizards:recurringScheduleError');
    setTimeout(() => (this.recurringScheduleError = ''), 5000);
  }

  getDayLabel(label: string | number): string {
    return (label as string).slice(0, 3);
  }
}
